import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { createBrowserTtsController } from '../lib/browserTts';
import { FREE_FLASHCARD_LIMIT } from '../lib/subscription';
import { fetchWebCheckoutUrl } from '../lib/webSubscription';
import { track } from '../lib/analytics';
import Select from './ui/Select';

const { getSearchRankedFlashcards } = require('../shared/flashcards');
const { priorityColors: PRIORITY_COLORS, success: COLOR_SUCCESS, destructive: COLOR_DESTRUCTIVE, hexToRgba } = require('../shared/colors');

const LANGUAGE_SALUTATIONS = {
  English: 'Hello',
  Spanish: 'Hola',
  Mandarin: '你好',
  Japanese: 'こんにちは',
  French: 'Bonjour',
  German: 'Hallo',
  Italian: 'Ciao',
  Korean: '안녕하세요',
  Portuguese: 'Olá',
  Arabic: 'مرحبا'
};

const BULK_SELECT_LONG_PRESS_MS = 500;
// The desktop table's sortable columns (native/study text, priority,
// proficiency, next review) shrink to just these two below `md` — mirroring
// frontend-mobile's ManageScreen, which only ever sorts by priority/
// proficiency. `MOBILE_SORT_QUERY` matches Tailwind's default `md` breakpoint
// (min-width: 768px), so "mobile" here is anything narrower.
const MOBILE_SORT_FIELDS = ['priority', 'proficiency'];
const MOBILE_SORT_QUERY = '(max-width: 767.98px)';
// Mirrors frontend-mobile's ReanimatedSwipeable config (manage.tsx): action
// panels are 100px wide, and swiping past half that width (50px) commits
// the action on release.
const MANAGE_SWIPE_ACTION_WIDTH = 100;
const MANAGE_SWIPE_TRIGGER_THRESHOLD = 50;
// A study-card touch that never travels further than this (in either axis) is
// a tap, not a drag. Chrome for Android suppresses the synthetic `click` once
// it treats a touch sequence as a scroll/drag, so the tap-to-flip has to be
// recognized from the touch events themselves instead of relying on `onClick`.
const STUDY_TAP_MOVE_THRESHOLD = 10;

const LANGUAGE_FONT_GROUP = {
  Mandarin: 'cjk', Japanese: 'cjk', Korean: 'cjk',
  Arabic: 'arabic',
  English: 'latin', Spanish: 'latin', French: 'latin',
  German: 'latin', Italian: 'latin', Portuguese: 'latin',
};

function Dashboard() {
  const { t } = useTranslation();
  const { user, getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('study');
  const [flashcards, setFlashcards] = useState([]);
  const [studyCards, setStudyCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNativeFirst, setShowNativeFirst] = useState(true);
  const [showSecondaryText, setShowSecondaryText] = useState(true);
  const [editingCard, setEditingCard] = useState(null);
  const [editForm, setEditForm] = useState({ nativeText: '', studyTextPrimary: '', studyTextSecondary: '', priority: 3 });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [addPriority, setAddPriority] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [bulkMode, setBulkMode] = useState('paste');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const limitReachedDialogRef = useRef(null);
  const limitReachedPreviousFocusRef = useRef(null);
  const [pendingGenerations, setPendingGenerations] = useState([]);
  const [nextReviewAt, setNextReviewAt] = useState(null);
  const [studyMode, setStudyMode] = useState('review');
  const [sessionTotal, setSessionTotal] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [studyTransitioning, setStudyTransitioning] = useState(false);

  const [manageSearch, setManageSearch] = useState('');
  const [manageSortCriteria, setManageSortCriteria] = useState([]);
  const MANAGE_CHUNK_SIZE = 100;
  const [manageVisibleCount, setManageVisibleCount] = useState(MANAGE_CHUNK_SIZE);
  const manageLoadMoreObserverRef = useRef(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState(new Set());
  const [lastSelectIndex, setLastSelectIndex] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressJustFiredRef = useRef(false);
  const manageSwipeStartRef = useRef(null);
  const manageSwipeJustFiredRef = useRef(false);
  const [swipingCardId, setSwipingCardId] = useState(null);
  const [manageSwipeOffset, setManageSwipeOffset] = useState(0);

  const cardRef = useRef(null);
  const studyGestureRef = useRef(null);
  const studyTapHandledRef = useRef(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [exitDirection, setExitDirection] = useState(0);
  const [cardKey, setCardKey] = useState(0);
  const [cardHistory, setCardHistory] = useState([]);
  const [showStudyEditModal, setShowStudyEditModal] = useState(false);
  const [studyEditForm, setStudyEditForm] = useState({ nativeText: '', studyTextPrimary: '', studyTextSecondary: '', priority: 3 });
  const [studyEditLoading, setStudyEditLoading] = useState(false);
  const [studyEditError, setStudyEditError] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingManageCardId, setSpeakingManageCardId] = useState(null);
  const [showExamples, setShowExamples] = useState(false);
  const [examplesLoading, setExamplesLoading] = useState(false);
  const [showExampleEditModal, setShowExampleEditModal] = useState(false);
  const [exampleEditIndex, setExampleEditIndex] = useState(null);
  const [exampleEditForm, setExampleEditForm] = useState({ nativeText: '', studyTextPrimary: '', studyTextSecondary: '' });
  const [exampleEditLoading, setExampleEditLoading] = useState(false);
  const [exampleEditError, setExampleEditError] = useState(null);
  const [expandedManageExamplesId, setExpandedManageExamplesId] = useState(null);
  const [manageExamplesLoadingId, setManageExamplesLoadingId] = useState(null);
  const [copiedCardId, setCopiedCardId] = useState(null);
  const [copiedSecondaryCardId, setCopiedSecondaryCardId] = useState(null);
  const [flashcardQuota, setFlashcardQuota] = useState(user?.flashcardQuota || null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [bgToast, setBgToast] = useState(null);
  const bgToastTimerRef = useRef(null);
  const pendingGenerationIdRef = useRef(0);
  const studyCardsRef = useRef([]);
  const studyAnswersRef = useRef(0);
  const ttsControllerRef = useRef(null);
  // Tracks the last ?view= value we've told the server about, so the effect below
  // never persists the same value twice — see updatePreferredView.
  const syncedViewParamRef = useRef(undefined);

  if (!ttsControllerRef.current) {
    ttsControllerRef.current = createBrowserTtsController();
  }

  const currentCard = studyCards[currentCardIndex];
  const flashcardLimit = flashcardQuota?.limit;
  const limitReached = flashcardLimit !== null && flashcardLimit !== undefined && flashcardQuota?.remaining === 0;

  const syncQuota = useCallback((quota) => {
    if (quota) {
      setFlashcardQuota(quota);
    }
  }, []);

  const showBackgroundToast = useCallback((type, message) => {
    setBgToast({ type, message });
    clearTimeout(bgToastTimerRef.current);
    bgToastTimerRef.current = setTimeout(() => setBgToast(null), 6000);
  }, []);

  const addPendingGeneration = useCallback((kind, count) => {
    const id = `pending-${++pendingGenerationIdRef.current}`;
    setPendingGenerations(prev => [{ id, kind, count }, ...prev]);
    return id;
  }, []);

  const removePendingGeneration = useCallback((id) => {
    setPendingGenerations(prev => prev.filter(p => p.id !== id));
  }, []);

  const getPendingGenerationLabel = useCallback((pending) => (
    pending.count
      ? t('manage.pendingGenerating', { count: pending.count })
      : t('manage.pendingGeneratingUnknown')
  ), [t]);

  useEffect(() => {
    return () => clearTimeout(bgToastTimerRef.current);
  }, []);

  useEffect(() => {
    studyCardsRef.current = studyCards;
  }, [studyCards]);

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const response = await fetchWebCheckoutUrl(getToken());
      track('purchase_started', { platform: 'web' });
      window.location.href = response.url;
    } catch {
      setUpgradeLoading(false);
    }
  };

  const handleQuotaGatedAction = (openModal) => {
    if (limitReached) {
      track('quota_limit_hit');
      setShowLimitReachedModal(true);
      return;
    }
    openModal();
  };

  useEffect(() => {
    if (showLimitReachedModal) {
      limitReachedPreviousFocusRef.current = document.activeElement;
      limitReachedDialogRef.current?.querySelector('button')?.focus();
    } else if (limitReachedPreviousFocusRef.current) {
      limitReachedPreviousFocusRef.current.focus?.();
      limitReachedPreviousFocusRef.current = null;
    }
  }, [showLimitReachedModal]);

  const handleLimitReachedKeyDown = (e) => {
    if (e.key !== 'Tab' || !limitReachedDialogRef.current) return;
    const focusable = limitReachedDialogRef.current.querySelectorAll('button');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const playAudio = (text, language, manageCardId = null) => {
    if (!text || !language) return;

    ttsControllerRef.current.speak({
      text,
      language,
      onStart: () => {
        setIsSpeaking(true);
        setSpeakingManageCardId(manageCardId);
      },
      onEnd: () => {
        setIsSpeaking(false);
        setSpeakingManageCardId(null);
      },
      onError: () => {
        setIsSpeaking(false);
        setSpeakingManageCardId(null);
      },
    });
  };

  const stopAudio = () => {
    ttsControllerRef.current.stop();
    setIsSpeaking(false);
    setSpeakingManageCardId(null);
  };

  const copyStudyText = useCallback(async (card) => {
    if (!navigator?.clipboard?.writeText || !card.studyTextPrimary) return;
    try {
      await navigator.clipboard.writeText(card.studyTextPrimary);
      setCopiedCardId(card._id);
      window.setTimeout(() => {
        setCopiedCardId((currentId) => (currentId === card._id ? null : currentId));
      }, 1500);
    } catch (error) {
      console.error('Error copying study text:', error);
    }
  }, []);

  const copySecondaryText = useCallback(async (card) => {
    if (!navigator?.clipboard?.writeText || !card.studyTextSecondary) return;
    try {
      await navigator.clipboard.writeText(card.studyTextSecondary);
      setCopiedSecondaryCardId(card._id);
      window.setTimeout(() => {
        setCopiedSecondaryCardId((currentId) => (currentId === card._id ? null : currentId));
      }, 1500);
    } catch (error) {
      console.error('Error copying study text:', error);
    }
  }, []);

  const fetchManageData = useCallback(async () => {
    const allRes = await axios.get(`${getApiBaseUrl()}/api/flashcards`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    setFlashcards(allRes.data.flashcards);
    syncQuota(allRes.data.quota);
  }, [getToken, syncQuota]);

  const fetchFlashcards = useCallback(async () => {
    try {
      const [, studyRes] = await Promise.all([
        fetchManageData(),
        axios.get(`${getApiBaseUrl()}/api/flashcards/study`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      setStudyCards(studyRes.data.flashcards);
      setSessionTotal(studyRes.data.flashcards.length);
      setReviewedCount(0);
      setNextReviewAt(studyRes.data.nextReviewAt);
      setStudyMode(studyRes.data.mode || 'review');
    } catch (error) {
      console.error('Error fetching flashcards:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchManageData, getToken]);

  const refreshAfterBackgroundInsert = useCallback(async () => {
    if (studyCardsRef.current.length === 0) {
      await fetchFlashcards();
      return;
    }
    try {
      await fetchManageData();
    } catch (error) {
      console.error('Error fetching flashcards:', error);
    }
  }, [fetchFlashcards, fetchManageData]);

  useEffect(() => {
    setFlashcardQuota(user?.flashcardQuota || null);
  }, [user?.flashcardQuota]);

  useEffect(() => {
    if (user) {
      setShowNativeFirst(user.showNativeFirst !== false);
      setShowSecondaryText(user.showSecondaryText !== false);
      fetchFlashcards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchFlashcards]);

  // Lets BottomNav (mounted separately in App.js) drive/reflect this view via
  // ?view=study|manage without lifting view state into a shared context — see
  // updatePreferredView. Merged into one effect (rather than a separate mount
  // effect plus a searchParams-watcher) because on a *fresh* mount both would
  // fire in the same pass, and comparing the incoming param against `view`
  // silently no-ops whenever it coincidentally matches the initial `useState('study')`
  // default — e.g. tapping the Study tab from another route while the stored
  // preference was 'manage' never persisted 'study' back to the server.
  // syncedViewParamRef (updated inside updatePreferredView too) is the single
  // source of truth for "have we already told the server about this exact
  // param value", so this never double-persists a value it just set itself.
  useEffect(() => {
    if (!user) return;
    const paramView = searchParams.get('view');
    const isValidParam = paramView === 'study' || paramView === 'manage';
    setView(isValidParam ? paramView : (user.preferredView || 'study'));

    if (isValidParam && syncedViewParamRef.current !== paramView) {
      updatePreferredView(paramView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  useEffect(() => {
    const detachVoiceListener = ttsControllerRef.current.bindVoiceChangeListener();

    return () => {
      detachVoiceListener();
      ttsControllerRef.current.stop({ preserveWarmup: false });
    };
  }, []);

  useEffect(() => {
    if (!user?.studyLanguage) {
      return;
    }

    ttsControllerRef.current.preload(user.studyLanguage, currentCard?.studyTextPrimary || '');
  }, [currentCard, user?.studyLanguage]);

  useEffect(() => {
    if (!user?.studyLanguage) {
      return;
    }

    const studySideVisible = currentCard && ((showNativeFirst && isFlipped) || (!showNativeFirst && !isFlipped));
    if (studySideVisible && currentCard?.studyTextPrimary) {
      ttsControllerRef.current.preload(user.studyLanguage, currentCard.studyTextPrimary);
    }
  }, [currentCard, isFlipped, showNativeFirst, user?.studyLanguage]);

  const updateShowNativeFirst = async (value) => {
    setShowNativeFirst(value);
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/auth/account`,
        { showNativeFirst: value },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (error) {
      console.error('Error updating preference:', error);
    }
  };

  const updateShowSecondaryText = async (value) => {
    setShowSecondaryText(value);
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/auth/account`,
        { showSecondaryText: value },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (error) {
      console.error('Error updating preference:', error);
    }
  };

  const getPrimaryFontSize = (language) => {
    const group = LANGUAGE_FONT_GROUP[language] || 'latin';
    if (group === 'cjk') return 'text-6xl md:text-7xl';
    if (group === 'arabic') return 'text-5xl md:text-6xl';
    return 'text-4xl md:text-5xl';
  };

  const getSecondaryFontSize = (language) => {
    const group = LANGUAGE_FONT_GROUP[language] || 'latin';
    if (group === 'cjk' || group === 'arabic') return 'text-xl md:text-2xl';
    return 'text-lg md:text-xl';
  };

  const updatePreferredView = async (newView) => {
    setView(newView);
    syncedViewParamRef.current = newView;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', newView);
      return next;
    }, { replace: true });
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/auth/account`,
        { preferredView: newView },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
    } catch (error) {
      console.error('Error updating view preference:', error);
    }
  };

  const formatTimeUntil = (dateString) => {
    if (!dateString) return null;
    const now = new Date();
    const target = new Date(dateString);
    const diffMs = target - now;

    if (diffMs <= 0) return t('time.now');

    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return t('time.days', { count: diffDays });
    if (diffHours > 0) return t('time.hours', { count: diffHours });
    if (diffMins > 0) return t('time.minutes', { count: diffMins });
    return t('time.seconds', { count: diffSecs });
  };

  const handleAnswer = (known) => {
    const card = studyCards[currentCardIndex];
    if (!card) return;

    setCardHistory(prev => [...prev, { card, index: currentCardIndex }]);
    studyAnswersRef.current += 1;
    if (studyAnswersRef.current === 1) track('study_session_started', { cards: studyCards.length, mode: studyMode });

    // Fire API call in background — don't block UI
    if (!card.practice) {
      axios.post(
        `${getApiBaseUrl()}/api/flashcards/${card._id}/review`,
        { quality: known ? 1 : 0 },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      ).catch(error => console.error('Error reviewing flashcard:', error));
    }

    // Update UI immediately
    setIsFlipped(false);
    setShowExamples(false);
    setReviewedCount(prev => prev + 1);
    const remainingCards = studyCards.filter((_, idx) => idx !== currentCardIndex);

    if (remainingCards.length === 0) {
      track('study_session_finished', { cards: studyAnswersRef.current, mode: studyMode });
      studyAnswersRef.current = 0;
      setStudyTransitioning(true);
      setStudyCards([]);
      setCardHistory([]);
      fetchFlashcards().then(() => setStudyTransitioning(false));
      setCurrentCardIndex(0);
    } else {
      setStudyCards(remainingCards);
      if (currentCardIndex >= remainingCards.length) {
        setCurrentCardIndex(0);
      }
    }
  };

  const handlePreviousCard = () => {
    if (cardHistory.length === 0 || isExiting) return;
    const prev = cardHistory[cardHistory.length - 1];
    setCardHistory(h => h.slice(0, -1));
    setStudyCards(cards => {
      const newCards = [...cards];
      newCards.splice(prev.index, 0, prev.card);
      return newCards;
    });
    setCurrentCardIndex(prev.index);
    setReviewedCount(r => Math.max(0, r - 1));
    setIsFlipped(false);
    setShowExamples(false);
    setCardKey(k => k + 1);
  };

  const toggleCardFace = () => {
    if (showExamples) {
      setShowExamples(false);
    } else {
      setIsFlipped(prev => !prev);
    }
  };

  const handleDragStart = (e) => {
    const isTouch = e.type === 'touchstart';
    const point = isTouch ? e.touches[0] : e;
    if (isTouch) studyTapHandledRef.current = false;
    studyGestureRef.current = { isTouch, x: point.clientX, y: point.clientY, moved: false };
    setDragStart(point.clientX);
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isDragging || dragStart === null) return;
    const point = e.type === 'touchmove' ? e.touches[0] : e;
    const gesture = studyGestureRef.current;
    if (gesture &&
        (Math.abs(point.clientX - gesture.x) > STUDY_TAP_MOVE_THRESHOLD ||
         Math.abs(point.clientY - gesture.y) > STUDY_TAP_MOVE_THRESHOLD)) {
      gesture.moved = true;
    }
    setDragOffset(point.clientX - dragStart);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    const gesture = studyGestureRef.current;
    studyGestureRef.current = null;

    if (Math.abs(dragOffset) > 100) {
      const direction = dragOffset > 0 ? 1 : -1;
      setExitDirection(direction);
      setIsExiting(true);
      setIsDragging(false);
      setDragStart(null);
      
      setTimeout(() => {
        handleAnswer(direction > 0);
        setIsExiting(false);
        setExitDirection(0);
        setDragOffset(0);
        setCardKey(prev => prev + 1);
      }, 300);
    } else {
      setDragStart(null);
      setDragOffset(0);
      setIsDragging(false);

      // Flip from `touchend` rather than waiting for the synthetic `click`,
      // which Chrome for Android drops whenever the gesture was claimed as a
      // scroll. `studyTapHandledRef` then swallows that click when it does
      // arrive; it is cleared by the next `touchstart` so a dropped click
      // cannot swallow a later real one.
      if (gesture && gesture.isTouch && !gesture.moved && !isExiting) {
        studyTapHandledRef.current = true;
        toggleCardFace();
      }
    }
  };

  const handleDragCancel = () => {
    studyGestureRef.current = null;
    setDragStart(null);
    setDragOffset(0);
    setIsDragging(false);
  };

  const handleButtonAnswer = (known) => {
    if (isExiting) return;
    const direction = known ? 1 : -1;
    setExitDirection(direction);
    setIsExiting(true);
    
    setTimeout(() => {
      handleAnswer(known);
      setIsExiting(false);
      setExitDirection(0);
      setDragOffset(0);
      setCardKey(prev => prev + 1);
    }, 300);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('manage.confirmDelete'))) return;

    try {
      const response = await axios.delete(`${getApiBaseUrl()}/api/flashcards/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      syncQuota(response.data?.quota);
      await fetchFlashcards();
    } catch (error) {
      console.error('Error deleting flashcard:', error);
    }
  };

  const toggleSelectCard = (id) => {
    setSelectedCardIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedCardIds(new Set());
    setLastSelectIndex(null);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowLongPress = (cardId, idx) => {
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      longPressJustFiredRef.current = true;
      setSelectMode(true);
      setEditingCard(null);
      setExpandedManageExamplesId(null);
      setSelectedCardIds(new Set([cardId]));
      setLastSelectIndex(idx);
    }, BULK_SELECT_LONG_PRESS_MS);
  };

  const resetManageSwipe = () => {
    manageSwipeStartRef.current = null;
    setSwipingCardId(null);
    setManageSwipeOffset(0);
  };

  // Applied to each foreground <td> of a swipeable Manage row (mobile only,
  // desktop leaves swipingCardId unset so this is always an identity
  // translateX there). Deliberately per-<td> rather than a wrapping element:
  // a <div> isn't valid as a direct child of <tr> and would trip React's
  // validateDOMNesting warning on every row.
  const manageSwipeStyle = (cardId) =>
    swipingCardId === cardId
      ? { transform: `translateX(${manageSwipeOffset}px)`, transition: 'none' }
      : { transform: 'translateX(0)', transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)' };

  // Mobile-only counterpart to the desktop Edit/Delete column: swipe right to
  // delete, swipe left to edit, mirroring frontend-mobile's ReanimatedSwipeable
  // row (manage.tsx). Shares the same touch lifecycle as the long-press-to-select
  // gesture, so a horizontal drag cancels the pending long-press timer.
  const handleManageRowTouchStart = (card, idx, e) => {
    // A previous gesture on this same card may not have reached touchend/
    // touchcancel (e.g. the OS interrupted the touch mid-swipe): clear any
    // leftover swipingCardId first so this new touch's long-press timer is
    // reliably cancelable by its own touchmove, rather than being silently
    // skipped because swipingCardId already (stale-)matched this card.
    resetManageSwipe();
    handleRowLongPress(card._id, idx);
    const touch = e.touches[0];
    manageSwipeStartRef.current = { x: touch.clientX, y: touch.clientY, cardId: card._id };
  };

  const handleManageRowTouchMove = (card, e) => {
    const start = manageSwipeStartRef.current;
    if (!start || start.cardId !== card._id) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (swipingCardId !== card._id) {
      // Any movement past jitter tolerance means this isn't a long-press held
      // in place, so cancel the pending timer regardless of direction —
      // otherwise a slow vertical scroll (finger stays down while the list
      // scrolls) can let the 500ms timer fire mid-scroll and enter bulk-select
      // mode. Only *commit* to a horizontal swipe (and start moving the card)
      // when the drag is predominantly horizontal; a predominantly vertical
      // drag still falls through as page scroll.
      if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;
      cancelLongPress();
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
      setSwipingCardId(card._id);
    }
    const clamped = Math.max(-MANAGE_SWIPE_ACTION_WIDTH, Math.min(MANAGE_SWIPE_ACTION_WIDTH, deltaX));
    setManageSwipeOffset(clamped);
  };

  const handleManageRowTouchEnd = (card) => {
    cancelLongPress();
    const wasSwiping = swipingCardId === card._id;
    const offset = manageSwipeOffset;
    resetManageSwipe();
    if (!wasSwiping) return;
    manageSwipeJustFiredRef.current = true;
    if (offset <= -MANAGE_SWIPE_TRIGGER_THRESHOLD) {
      handleEdit(card);
    } else if (offset >= MANAGE_SWIPE_TRIGGER_THRESHOLD) {
      handleDelete(card._id);
    }
  };

  const handleSelectClick = (cardId, idx, e) => {
    if (longPressJustFiredRef.current) {
      longPressJustFiredRef.current = false;
      return;
    }
    if (e.shiftKey && lastSelectIndex !== null) {
      const start = Math.min(lastSelectIndex, idx);
      const end = Math.max(lastSelectIndex, idx);
      const rangeIds = visibleFlashcards.slice(start, end + 1).map(c => c._id);
      setSelectedCardIds(prev => new Set([...prev, ...rangeIds]));
    } else {
      toggleSelectCard(cardId);
      setLastSelectIndex(idx);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCardIds.size === 0) return;
    if (!window.confirm(t('manage.confirmBulkDelete', { count: selectedCardIds.size }))) return;
    try {
      const response = await axios.delete(`${getApiBaseUrl()}/api/flashcards/bulk-delete`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        data: { ids: Array.from(selectedCardIds) },
      });
      syncQuota(response.data?.quota);
      exitSelectMode();
      await fetchFlashcards();
    } catch (error) {
      console.error('Error bulk deleting flashcards:', error);
    }
  };

  const handleEdit = (card) => {
    setEditingCard(card._id);
    setEditForm({
      nativeText: card.nativeText,
      studyTextPrimary: card.studyTextPrimary,
      studyTextSecondary: card.studyTextSecondary || '',
      priority: card.priority || 3
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/flashcards/${editingCard}`,
        editForm,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setEditingCard(null);
      await fetchFlashcards();
    } catch (error) {
      console.error('Error updating flashcard:', error);
    }
  };

  const handleStudyEditSave = async () => {
    if (!currentCard) return;
    setStudyEditLoading(true);
    setStudyEditError(null);
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/flashcards/${currentCard._id}`,
        { nativeText: studyEditForm.nativeText, studyTextPrimary: studyEditForm.studyTextPrimary, studyTextSecondary: studyEditForm.studyTextSecondary, priority: studyEditForm.priority },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setStudyCards(cards => cards.map(c => c._id === currentCard._id ? { ...c, nativeText: studyEditForm.nativeText, studyTextPrimary: studyEditForm.studyTextPrimary, studyTextSecondary: studyEditForm.studyTextSecondary, priority: studyEditForm.priority } : c));
      setShowStudyEditModal(false);
    } catch (error) {
      console.error('Error updating flashcard:', error);
      setStudyEditError(error.response?.data?.error || t('manage.failedToUpdateFlashcard'));
    } finally {
      setStudyEditLoading(false);
    }
  };

  const handleExampleEditSave = async () => {
    if (!currentCard || exampleEditIndex === null) return;
    setExampleEditLoading(true);
    setExampleEditError(null);
    try {
      const res = await axios.put(
        `${getApiBaseUrl()}/api/flashcards/${currentCard._id}/examples/${exampleEditIndex}`,
        { nativeText: exampleEditForm.nativeText, studyTextPrimary: exampleEditForm.studyTextPrimary, studyTextSecondary: exampleEditForm.studyTextSecondary || undefined },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setStudyCards(cards => cards.map(c => c._id === currentCard._id ? { ...c, examples: res.data.flashcard.examples } : c));
      setShowExampleEditModal(false);
    } catch (error) {
      console.error('Error updating example:', error);
      setExampleEditError(error.response?.data?.error || t('manage.failedToUpdateExample'));
    } finally {
      setExampleEditLoading(false);
    }
  };

  const handleManageGenerateExamples = async (card) => {
    setManageExamplesLoadingId(card._id);
    try {
      const res = await axios.post(
        `${getApiBaseUrl()}/api/flashcards/${card._id}/examples`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setFlashcards(cards => cards.map(c => c._id === card._id ? { ...c, examples: res.data.flashcard.examples } : c));
      setStudyCards(cards => cards.map(c => c._id === card._id ? { ...c, examples: res.data.flashcard.examples } : c));
    } catch (err) {
      console.error('Error generating examples:', err);
    } finally {
      setManageExamplesLoadingId(null);
    }
  };

  const handleAddFlashcard = () => {
    if (!addInput.trim()) return;

    const inputText = addInput.trim();
    const priority = addPriority ? parseInt(addPriority) : undefined;

    setShowAddModal(false);
    setAddInput('');
    setAddPriority('');

    const pendingId = addPendingGeneration('add', 1);

    (async () => {
      try {
        const body = { inputText };
        if (priority) body.priority = priority;
        const response = await axios.post(
          `${getApiBaseUrl()}/api/flashcards/generate`,
          body,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        syncQuota(response.data?.quota);
        track('flashcard_create_succeeded', { method: 'add', count: 1 });
        showBackgroundToast('success', t('manage.addedToast', { word: inputText }));
        await refreshAfterBackgroundInsert();
      } catch (error) {
        console.error('Error adding flashcard:', error);
        track('flashcard_create_failed', { method: 'add', status: error.response?.status ?? 0 });
        syncQuota(error.response?.data?.quota);
        const message = error.response?.data?.error || t('manage.failedToGenerateFlashcard');
        showBackgroundToast('error', t('manage.addFailedToast', { word: inputText, message }));
      } finally {
        removePendingGeneration(pendingId);
      }
    })();
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddInput('');
    setAddPriority('');
  };

  const handleBulkImport = () => {
    let payload;
    if (bulkMode === 'file' && bulkFile) {
      payload = { file: bulkFile };
    } else if (bulkMode === 'paste' && bulkText.trim()) {
      payload = { text: bulkText.trim() };
    } else {
      setBulkError(bulkMode === 'file' ? t('manage.pleaseSelectFile') : t('manage.pleasePasteText'));
      return;
    }

    setShowBulkModal(false);
    setBulkText('');
    setBulkFile(null);
    setBulkError(null);
    setBulkMode('paste');

    const pendingId = addPendingGeneration('bulk', null);

    (async () => {
      try {
        const formData = new FormData();
        if (payload.file) {
          formData.append('file', payload.file);
        } else {
          formData.append('text', payload.text);
        }

        const response = await axios.post(
          `${getApiBaseUrl()}/api/flashcards/bulk-import`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        syncQuota(response.data?.quota);
        track('flashcard_create_succeeded', { method: 'bulk', count: response.data.imported });
        const skipped = response.data.skipped > 0 ? t('manage.skippedSuffix', { count: response.data.skipped }) : '';
        showBackgroundToast('success', t('manage.importFinishedToast', { count: response.data.imported, skipped }));
        await refreshAfterBackgroundInsert();
      } catch (error) {
        console.error('Error bulk importing:', error);
        track('flashcard_create_failed', { method: 'bulk', status: error.response?.status ?? 0 });
        syncQuota(error.response?.data?.quota);
        const message = error.response?.data?.error || t('manage.failedToImportVocabulary');
        showBackgroundToast('error', t('manage.importFailedToast', { message }));
      } finally {
        removePendingGeneration(pendingId);
      }
    })();
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkText('');
    setBulkFile(null);
    setBulkError(null);
    setBulkMode('paste');
  };

  const handleGenerateBatch = () => {
    setShowGenerateModal(false);

    const pendingId = addPendingGeneration('generate', 5);

    (async () => {
      try {
        const response = await axios.post(
          `${getApiBaseUrl()}/api/flashcards/generate-batch`,
          {},
          { headers: { Authorization: `Bearer ${getToken()}` }, timeout: 120000 }
        );

        syncQuota(response.data?.quota);
        track('flashcard_create_succeeded', { method: 'generate', count: response.data.imported });
        const skipped = response.data.skipped > 0 ? t('manage.skippedSuffix', { count: response.data.skipped }) : '';
        showBackgroundToast('success', t('manage.generateFinishedToast', { count: response.data.imported, skipped }));
        await refreshAfterBackgroundInsert();
      } catch (error) {
        console.error('Error generating vocabulary:', error);
        track('flashcard_create_failed', { method: 'generate', status: error.response?.status ?? 0 });
        syncQuota(error.response?.data?.quota);
        const message = error.response?.data?.error || t('manage.failedToGenerateVocabulary');
        showBackgroundToast('error', t('manage.generateFailedToast', { message }));
      } finally {
        removePendingGeneration(pendingId);
      }
    })();
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
  };

  const handleManageSort = (field) => {
    setManageSortCriteria(prev => {
      const idx = prev.findIndex(c => c.field === field);
      if (idx === -1) {
        return [...prev, { field, dir: 'asc' }];
      }
      if (prev[idx].dir === 'asc') {
        return prev.map((c, i) => i === idx ? { ...c, dir: 'desc' } : c);
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const getSortValue = (card, field) => {
    if (field === 'nativeText') return card.nativeText.toLowerCase();
    if (field === 'studyText') return (card.studyTextSecondary || card.studyTextPrimary).toLowerCase();
    if (field === 'priority') return card.priority;
    if (field === 'proficiency') return card.proficiency;
    if (field === 'nextReviewAt') return card.nextReviewAt ? new Date(card.nextReviewAt).getTime() : Infinity;
    return 0;
  };

  const filteredFlashcards = useMemo(() => {
    const rankedResults = getSearchRankedFlashcards(flashcards, manageSearch.trim());
    const searchScores = new Map(rankedResults.map(({ card, searchScore }) => [card._id, searchScore]));
    let result = rankedResults.map(({ card }) => card);

    if (manageSortCriteria.length > 0) {
      result = [...result].sort((a, b) => {
        for (const { field, dir } of manageSortCriteria) {
          const aVal = getSortValue(a, field);
          const bVal = getSortValue(b, field);
          if (aVal < bVal) return dir === 'asc' ? -1 : 1;
          if (aVal > bVal) return dir === 'asc' ? 1 : -1;
        }
        const aSearchScore = searchScores.get(a._id) ?? Number.POSITIVE_INFINITY;
        const bSearchScore = searchScores.get(b._id) ?? Number.POSITIVE_INFINITY;
        if (aSearchScore !== bSearchScore) {
          return aSearchScore - bSearchScore;
        }
        return 0;
      });
    }

    return result;
  }, [flashcards, manageSearch, manageSortCriteria]);

  const visibleFlashcards = useMemo(() => (
    filteredFlashcards.slice(0, manageVisibleCount)
  ), [filteredFlashcards, manageVisibleCount]);
  const hasMoreFlashcards = manageVisibleCount < filteredFlashcards.length;

  // Fires when the sentinel div at the end of the manage list scrolls into
  // view, revealing the next chunk. A callback ref (re)creates the observer
  // whenever the sentinel mounts/unmounts, which happens naturally whenever
  // hasMoreFlashcards flips (e.g. all cards loaded, or a narrower search
  // brings the sentinel back).
  const manageLoadMoreRef = useCallback((node) => {
    if (manageLoadMoreObserverRef.current) {
      manageLoadMoreObserverRef.current.disconnect();
      manageLoadMoreObserverRef.current = null;
    }
    if (!node) return;
    manageLoadMoreObserverRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setManageVisibleCount(prev => prev + MANAGE_CHUNK_SIZE);
      }
    }, { rootMargin: '400px' });
    manageLoadMoreObserverRef.current.observe(node);
  }, []);

  const nativeLanguageLabel = user?.nativeLanguage || 'Native';
  const studyLanguageLabel = user?.studyLanguage || 'Study';
  const addInputPlaceholder = t('manage.addInputPlaceholderTemplate', {
    study: LANGUAGE_SALUTATIONS[studyLanguageLabel] || studyLanguageLabel,
    native: LANGUAGE_SALUTATIONS[nativeLanguageLabel] || nativeLanguageLabel,
  });
  const manageColumnLabels = [
    { field: 'nativeText', label: `${nativeLanguageLabel} ${t('manage.columnTextSuffix')}`, align: 'text-left' },
    { field: 'studyText', label: `${studyLanguageLabel} ${t('manage.columnTextSuffix')}`, align: 'text-left' },
    { field: 'priority', label: t('flashcardModals.addCard.priorityLabel'), align: 'text-center' },
    { field: 'proficiency', label: t('manage.columnProficiency'), align: 'text-center' },
    { field: 'nextReviewAt', label: t('manage.columnNextReview'), align: 'text-center' },
  ];

  // Search or sort reshuffles which cards should be visible first, so reveal
  // from the top again and drop any shift-select anchor tied to the old order.
  useEffect(() => {
    setManageVisibleCount(MANAGE_CHUNK_SIZE);
    setLastSelectIndex(null);
  }, [manageSearch, manageSortCriteria]);

  // The mobile sort row only exposes priority/proficiency. If a
  // desktop-only criterion (native text, study text, next review) was set
  // before the viewport narrowed below `md`, drop it so the mobile row's
  // active/inactive state stays truthful and every remaining criterion is
  // one the mobile controls can actually toggle off again.
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_SORT_QUERY);
    const clearUnsupportedMobileSort = () => {
      setManageSortCriteria(prev => {
        const next = prev.filter(c => MOBILE_SORT_FIELDS.includes(c.field));
        return next.length === prev.length ? prev : next;
      });
    };
    const handleChange = (e) => {
      if (e.matches) clearUnsupportedMobileSort();
    };
    if (mql.matches) clearUnsupportedMobileSort();
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (view !== 'manage') {
      setSelectMode(false);
      setSelectedCardIds(new Set());
      setExpandedManageExamplesId(null);
    }
  }, [view]);

  if (!user || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-row flex-wrap justify-center md:justify-between items-center gap-3 sm:gap-4 mb-3 md:mb-6">
            {/* Redundant with BottomNav below md, which already switches Study/Manage. */}
            <div className="hidden md:flex bg-muted rounded-lg p-0.5 sm:p-1">
              <button
                onClick={() => updatePreferredView('study')}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  view === 'study' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('nav.study')}
              </button>
              <button
                onClick={() => updatePreferredView('manage')}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  view === 'manage'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('nav.manage')}
              </button>
            </div>

            {view === 'study' && studyCards.length > 0 && (
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex bg-muted rounded-lg p-0.5 sm:p-1" title={t('dashboard.showFirstTitle')}>
                  <button
                    onClick={() => updateShowNativeFirst(true)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                      showNativeFirst
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {user?.nativeLanguage || 'Native'}
                  </button>
                  <button
                    onClick={() => updateShowNativeFirst(false)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                      !showNativeFirst
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {user?.studyLanguage || 'Study'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {view === 'study' && (
            <div className="flex flex-col items-center">
              {flashcards.length === 0 ? (
                <div className="bg-card rounded-lg shadow-md p-8 border border-border text-center w-full">
                  <div className="text-6xl mb-4">📚</div>
                  <h2 className="text-2xl font-bold text-foreground mb-4">{t('study.emptyTitle')}</h2>
                  <p className="text-muted-foreground mb-6">
                    {t('study.emptyBody')}
                  </p>
                  <button
                    onClick={() => updatePreferredView('manage')}
                    className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                  >
                    {t('study.manageFlashcards')}
                  </button>
                </div>
              ) : studyCards.length === 0 || studyTransitioning ? (
                <div className="flex flex-col items-center justify-center min-h-[350px] gap-3">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  {studyTransitioning && (
                    <p className="text-sm text-muted-foreground">{t('study.loadingNext')}</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center text-sm text-muted-foreground mb-4">
                    <div className="relative">
                      <button
                        onClick={handlePreviousCard}
                        disabled={cardHistory.length === 0 || isExiting}
                        className="absolute right-full top-1/2 -translate-y-1/2 mr-1 p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={t('study.previousCard')}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span>
                        {studyMode === 'review' ? (
                          <>{t('study.cardOf', { current: reviewedCount + 1, total: sessionTotal })}</>
                        ) : studyMode === 'practice' ? (
                          <>{nextReviewAt ? t('study.practiceModeNextReview', { time: formatTimeUntil(nextReviewAt) }) : t('study.practiceMode')}</>
                        ) : (
                          <>{t('study.allMastered')}</>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="relative w-full max-w-sm md:max-w-xl mx-auto overflow-visible">
                    <div
                      key={cardKey}
                      ref={cardRef}
                      onClick={() => {
                        if (studyTapHandledRef.current) {
                          studyTapHandledRef.current = false;
                          return;
                        }
                        if (isDragging || isExiting) return;
                        toggleCardFace();
                      }}
                      onMouseDown={!isExiting ? handleDragStart : undefined}
                      onMouseMove={handleDragMove}
                      onMouseUp={handleDragEnd}
                      onMouseLeave={handleDragEnd}
                      onTouchStart={!isExiting ? handleDragStart : undefined}
                      onTouchMove={handleDragMove}
                      onTouchEnd={handleDragEnd}
                      onTouchCancel={handleDragCancel}
                      data-testid="study-card"
                      className={`relative bg-card rounded-xl shadow-lg border p-8 ${showExamples ? 'h-[500px] md:h-[525px]' : 'min-h-[500px] md:min-h-[525px]'} flex flex-col items-center justify-center cursor-pointer select-none touch-pan-y`}
                      style={{
                        transform: isExiting 
                          ? `translateX(${exitDirection * 500}px) rotate(${exitDirection * 30}deg) scale(0.8)` 
                          : `translateX(${dragOffset}px) rotate(${dragOffset * 0.05}deg)`,
                        opacity: isExiting ? 0 : 1,
                        transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out, box-shadow 0.2s ease-out, border-color 0.2s ease-out',
                        animation: !isExiting && !isDragging ? 'cardEnter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
                        borderColor: dragOffset > 50 ? COLOR_SUCCESS : dragOffset < -50 ? COLOR_DESTRUCTIVE : undefined,
                        boxShadow: dragOffset > 50
                          ? `0 0 20px ${hexToRgba(COLOR_SUCCESS, 0.4)}, 0 0 40px ${hexToRgba(COLOR_SUCCESS, 0.2)}`
                          : dragOffset < -50
                            ? `0 0 20px ${hexToRgba(COLOR_DESTRUCTIVE, 0.4)}, 0 0 40px ${hexToRgba(COLOR_DESTRUCTIVE, 0.2)}`
                            : undefined,
                      }}
                    >
                      {currentCard && (
                        <>
                          {!showExamples && <button
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              setStudyEditForm({
                                nativeText: currentCard.nativeText,
                                studyTextPrimary: currentCard.studyTextPrimary,
                                studyTextSecondary: currentCard.studyTextSecondary || '',
                                priority: currentCard.priority || 3
                              });
                              setStudyEditError(null);
                              setShowStudyEditModal(true);
                            }}
                            className="absolute top-3 right-3 p-2.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors z-10"
                            title={t('study.editCardTitle')}
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>}
                          <p className="absolute top-3 left-0 right-0 text-xs text-muted-foreground text-center">
                            {showExamples ? t('study.examplesBackDrag') : t('study.hintDrag')}
                          </p>
                          <div className="flex-1 min-h-0 flex flex-col items-center justify-center w-full relative">
                            {showExamples ? (
                              <div className="flex flex-col w-full flex-1 overflow-y-auto justify-evenly py-2 px-1">
                                {(currentCard.examples || []).map((ex, i) => (
                                  <div key={i} className="border border-border rounded-lg px-4 py-3 space-y-0.5 relative">
                                    <button
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExampleEditIndex(i);
                                        setExampleEditForm({
                                          nativeText: ex.nativeText,
                                          studyTextPrimary: ex.studyTextPrimary,
                                          studyTextSecondary: ex.studyTextSecondary || '',
                                        });
                                        setExampleEditError(null);
                                        setShowExampleEditModal(true);
                                      }}
                                      className="absolute top-2 right-2 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                      title={t('study.editExampleTitle')}
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                    <p className="text-xl font-semibold text-foreground pr-7">{ex.studyTextPrimary}</p>
                                    {ex.studyTextSecondary && (
                                      <p className="text-sm text-muted-foreground">{ex.studyTextSecondary}</p>
                                    )}
                                    {ex.nativeText !== ex.studyTextPrimary && (
                                      <p className="text-sm text-muted-foreground">{ex.nativeText}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (showNativeFirst && !isFlipped) || (!showNativeFirst && isFlipped) ? (
                              <p className={`${getPrimaryFontSize(currentCard.nativeLanguage)} font-bold text-foreground text-center`}>
                                {currentCard.nativeText}
                              </p>
                            ) : (
                              <>
                                {currentCard.studyTextSecondary && (
                                  <div
                                    className="cursor-pointer select-none mb-3 mt-3"
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onTouchStart={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateShowSecondaryText(!showSecondaryText);
                                    }}
                                    data-testid="toggle-secondary-button"
                                    title={showSecondaryText ? t('study.hideSecondaryText') : t('study.showSecondaryText')}
                                  >
                                    <div className="flex items-center justify-center px-4 py-2 rounded-lg hover:bg-muted/30 transition-colors">
                                      {showSecondaryText ? (
                                        <p className={`${getSecondaryFontSize(currentCard.studyLanguage)} text-muted-foreground max-w-[200px] md:max-w-[300px] text-center`}>
                                          {currentCard.studyTextSecondary}
                                        </p>
                                      ) : (
                                        <span aria-hidden="true" className={`${getSecondaryFontSize(currentCard.studyLanguage)} inline-block bg-muted-foreground/20 rounded text-transparent select-none max-w-[200px] md:max-w-[300px] text-center`}>
                                          {currentCard.studyTextSecondary}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div
                                  className="rounded-xl px-6 py-3"
                                  data-testid="primary-text-audio-button"
                                >
                                  <span className={`${getPrimaryFontSize(currentCard.studyLanguage)} font-bold text-foreground text-center block`}>
                                    {currentCard.studyTextPrimary}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                          {((showNativeFirst && isFlipped) || (!showNativeFirst && !isFlipped)) && !showExamples && (
                            <button
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (currentCard.examples && currentCard.examples.length > 0) {
                                  setShowExamples(true);
                                  return;
                                }
                                setExamplesLoading(true);
                                axios.post(
                                  `${getApiBaseUrl()}/api/flashcards/${currentCard._id}/examples`,
                                  {},
                                  { headers: { Authorization: `Bearer ${getToken()}` } }
                                ).then(res => {
                                  setStudyCards(prev => prev.map(c =>
                                    c._id === currentCard._id ? { ...c, examples: res.data.flashcard.examples } : c
                                  ));
                                  setShowExamples(true);
                                }).catch(err => {
                                  console.error('Error fetching examples:', err);
                                }).finally(() => {
                                  setExamplesLoading(false);
                                });
                              }}
                              className="absolute bottom-3 left-3 p-2.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors z-10"
                              title={t('study.showExamples')}
                              data-testid="examples-button"
                            >
                              {examplesLoading ? (
                                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10" />
                                </svg>
                              )}
                            </button>
                          )}
                          {((showNativeFirst && isFlipped) || (!showNativeFirst && !isFlipped)) && !showExamples && (
                            <button
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSpeaking) {
                                  stopAudio();
                                } else {
                                  playAudio(currentCard.studyTextPrimary, user?.studyLanguage);
                                }
                              }}
                              className="absolute bottom-3 right-3 p-2.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors z-10"
                              title={isSpeaking ? t('study.stopAudio') : t('study.playAudio')}
                              data-testid="audio-button"
                            >
                              {isSpeaking ? (
                                <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-muted-foreground hover:text-foreground transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                </svg>
                              )}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6 w-full max-w-sm md:max-w-xl mx-auto">
                    <button
                      onClick={() => handleButtonAnswer(false)}
                      disabled={isExiting}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-error-bg border border-error-border text-destructive rounded-lg font-semibold hover:bg-error-bg/80 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {t('study.dontKnow')}
                    </button>
                    <button
                      onClick={() => handleButtonAnswer(true)}
                      disabled={isExiting}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-success-bg border border-success-border text-success rounded-lg font-semibold hover:bg-success-bg/80 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('study.know')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-foreground">{t('flashcardModals.addCard.title')}</h3>
                  <button
                    onClick={closeAddModal}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  {t('flashcardModals.addCard.instructions', { native: user.nativeLanguage, study: user.studyLanguage })}
                </p>

                <p className="text-xs text-muted-foreground mb-2 bg-muted/50 p-2 rounded">
                  <strong>{t('flashcardModals.addCard.tip')}</strong> {t('flashcardModals.addCard.tipBody')}
                </p>
                
                <div className="space-y-4">
                  <div>
                    <input
                      type="text"
                      value={addInput}
                      onChange={(e) => setAddInput(e.target.value.slice(0, 50))}
                      placeholder={addInputPlaceholder}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground text-lg"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddFlashcard();
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                      {t('flashcardModals.addCard.charCount', { count: addInput.length })}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">{t('flashcardModals.addCard.priorityLabel')} <span className="text-xs">{t('flashcardModals.addCard.priorityOptionalHint')}</span></label>
                    <Select
                      value={addPriority}
                      onChange={(e) => setAddPriority(e.target.value)}
                      size="sm"
                      data-testid="add-priority-select"
                    >
                      <option value="">{t('flashcardModals.addCard.priorityAuto')}</option>
                      <option value="1">{t('flashcardModals.addCard.priorityHigh')}</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">{t('flashcardModals.addCard.priorityLow')}</option>
                    </Select>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={closeAddModal}
                      className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      data-track="add-submit"
                      onClick={handleAddFlashcard}
                      disabled={!addInput.trim()}
                      className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.add')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showBulkModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-foreground">{t('flashcardModals.bulkImport.title')}</h3>
                  <button
                    onClick={closeBulkModal}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    <strong>{t('flashcardModals.bulkImport.instructions')}</strong><br/>
                    1. <strong>{t('flashcardModals.bulkImport.instructionRequest')}</strong> {t('flashcardModals.bulkImport.instructionRequestExample')}<br/>
                    2. <strong>{t('flashcardModals.bulkImport.instructionExtract')}</strong> {t('flashcardModals.bulkImport.instructionExtractBody')}<br/><br/>
                    {t('flashcardModals.bulkImport.instructionLimit')}
                  </p>

                  <div className="flex bg-muted rounded-lg p-1">
                    <button
                      onClick={() => setBulkMode('paste')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        bulkMode === 'paste'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t('flashcardModals.bulkImport.pasteTab')}
                    </button>
                    <button
                      onClick={() => setBulkMode('file')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        bulkMode === 'file'
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t('flashcardModals.bulkImport.uploadTab')}
                    </button>
                  </div>

                  {bulkMode === 'paste' ? (
                    <div>
                      <textarea
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value.slice(0, 50000))}
                        placeholder={t('flashcardModals.bulkImport.pastePlaceholder')}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground text-sm min-h-[200px] resize-y"
                      />
                      <p className="text-xs text-muted-foreground mt-1 text-right">
                        {t('flashcardModals.bulkImport.pasteCharCount', { count: bulkText.length.toLocaleString() })}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors bg-background">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg className="w-8 h-8 mb-2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          {bulkFile ? (
                            <p className="text-sm text-foreground font-medium">{bulkFile.name}</p>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {t('flashcardModals.bulkImport.uploadPrompt')}
                            </p>
                          )}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".txt,.pdf,.json"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) setBulkFile(file);
                          }}
                        />
                      </label>
                      {bulkFile && (
                        <div className="flex items-center justify-between mt-2 text-sm">
                          <span className="text-muted-foreground">
                            {(bulkFile.size / 1024).toFixed(1)} KB
                          </span>
                          <button
                            onClick={() => setBulkFile(null)}
                            className="text-destructive hover:text-destructive/80 text-sm"
                          >
                            {t('flashcardModals.bulkImport.remove')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {bulkError && (
                    <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm">
                      {bulkError}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={closeBulkModal}
                      className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      data-track="bulk-submit"
                      onClick={handleBulkImport}
                      disabled={bulkMode === 'paste' ? !bulkText.trim() : !bulkFile}
                      className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.import')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showGenerateModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-foreground">{t('flashcardModals.generate.title')}</h3>
                  <button
                    onClick={closeGenerateModal}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('flashcardModals.generate.confirmBody')}
                  </p>
                  <div className="bg-muted/50 border border-border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-1">{t('flashcardModals.generate.yourProficiencyLevel')}</p>
                    <p className="text-lg font-bold text-foreground">
                      {t('flashcardModals.generate.levelLabel', { level: user.studyLanguageProficiency || 1 })}
                      <span className="text-sm font-normal text-muted-foreground ml-2">
                        ({t(`proficiency.${user.studyLanguageProficiency || 1}`)})
                      </span>
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('flashcardModals.generate.accountSettingsHint')}
                  </p>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={closeGenerateModal}
                      className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      data-testid="confirm-generate"
                      data-track="generate-submit"
                      onClick={handleGenerateBatch}
                      className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                    >
                      {t('common.generate')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showLimitReachedModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div
                ref={limitReachedDialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="limit-reached-title"
                onKeyDown={handleLimitReachedKeyDown}
                className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary flex-shrink-0">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <button
                    onClick={() => setShowLimitReachedModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <h3 id="limit-reached-title" className="text-lg font-bold text-foreground mb-2">{t('flashcardModals.limitReached.title')}</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('flashcardModals.limitReached.body', { limit: flashcardQuota?.limit || FREE_FLASHCARD_LIMIT })}
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLimitReachedModal(false)}
                    className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    data-track="subscribe"
                    onClick={handleUpgrade}
                    disabled={upgradeLoading}
                    className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                  >
                    {upgradeLoading ? t('common.loading') : t('account.subscription.upgradeToPremium')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showGuideModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-foreground">{t('manage.guideTitle')}</h3>
                  <button
                    onClick={() => setShowGuideModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="hidden max-md:flex items-start gap-3 mb-4">
                  <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h18M16.5 3L21 7.5m0 0L16.5 12M21 7.5H3" />
                  </svg>
                  <p className="text-sm text-muted-foreground">{t('manage.guideTipSwipeMobile')}</p>
                </div>

                <div className="flex items-start gap-3 mb-4">
                  <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                  </svg>
                  <p className="text-sm text-muted-foreground">{t('manage.guideTipMultiSelectWeb')}</p>
                </div>

                <button
                  onClick={() => setShowGuideModal(false)}
                  className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
                  data-testid="manage-guide-done-button"
                >
                  {t('common.done')}
                </button>
              </div>
            </div>
          )}

          {showStudyEditModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-foreground">{t('flashcardModals.editCard.title')}</h3>
                  <button
                    onClick={() => setShowStudyEditModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{user.nativeLanguage}</label>
                    <input
                      type="text"
                      value={studyEditForm.nativeText}
                      onChange={(e) => setStudyEditForm({ ...studyEditForm, nativeText: e.target.value.slice(0, 50) })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                      disabled={studyEditLoading}
                      maxLength={50}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{studyEditForm.nativeText.length}/50</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{user.studyLanguage} {t('flashcardModals.editCard.primarySuffix')}</label>
                    <input
                      type="text"
                      value={studyEditForm.studyTextPrimary}
                      onChange={(e) => setStudyEditForm({ ...studyEditForm, studyTextPrimary: e.target.value.slice(0, 50) })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                      disabled={studyEditLoading}
                      maxLength={50}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{studyEditForm.studyTextPrimary.length}/50</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{user.studyLanguage} {t('flashcardModals.editCard.secondarySuffix')}</label>
                    <input
                      type="text"
                      value={studyEditForm.studyTextSecondary}
                      onChange={(e) => setStudyEditForm({ ...studyEditForm, studyTextSecondary: e.target.value.slice(0, 50) })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                      placeholder={t('common.optional')}
                      disabled={studyEditLoading}
                      maxLength={50}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{studyEditForm.studyTextSecondary.length}/50</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('flashcardModals.addCard.priorityLabel')}</label>
                    <Select
                      value={studyEditForm.priority}
                      onChange={(e) => setStudyEditForm({ ...studyEditForm, priority: parseInt(e.target.value) })}
                      disabled={studyEditLoading}
                    >
                      <option value={1}>{t('flashcardModals.editCard.priorityHighShort')}</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                      <option value={4}>4</option>
                      <option value={5}>{t('flashcardModals.editCard.priorityLowShort')}</option>
                    </Select>
                  </div>
                  {studyEditError && (
                    <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm">
                      {studyEditError}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowStudyEditModal(false)}
                      className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                      disabled={studyEditLoading}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleStudyEditSave}
                      disabled={!studyEditForm.nativeText.trim() || !studyEditForm.studyTextPrimary.trim() || studyEditLoading}
                      className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {studyEditLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('flashcardModals.editCard.saving')}
                        </span>
                      ) : (
                        t('common.save')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showExampleEditModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-foreground">{t('flashcardModals.editExample.title')}</h3>
                  <button
                    onClick={() => setShowExampleEditModal(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{user.studyLanguage} {t('flashcardModals.editCard.primarySuffix')}</label>
                    <input
                      type="text"
                      value={exampleEditForm.studyTextPrimary}
                      onChange={(e) => setExampleEditForm({ ...exampleEditForm, studyTextPrimary: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                      disabled={exampleEditLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{user.studyLanguage} {t('flashcardModals.editCard.secondarySuffix')}</label>
                    <input
                      type="text"
                      value={exampleEditForm.studyTextSecondary}
                      onChange={(e) => setExampleEditForm({ ...exampleEditForm, studyTextSecondary: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                      placeholder={t('common.optional')}
                      disabled={exampleEditLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{user.nativeLanguage}</label>
                    <input
                      type="text"
                      value={exampleEditForm.nativeText}
                      onChange={(e) => setExampleEditForm({ ...exampleEditForm, nativeText: e.target.value })}
                      className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                      disabled={exampleEditLoading}
                    />
                  </div>
                  {exampleEditError && (
                    <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm">
                      {exampleEditError}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowExampleEditModal(false)}
                      className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                      disabled={exampleEditLoading}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleExampleEditSave}
                      disabled={!exampleEditForm.nativeText.trim() || !exampleEditForm.studyTextPrimary.trim() || exampleEditLoading}
                      className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {exampleEditLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('flashcardModals.editCard.saving')}
                        </span>
                      ) : (
                        t('common.save')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {view === 'manage' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-xl font-bold text-foreground">
                      {t('manage.yourFlashcards', { count: flashcards.length })}
                    </h2>
                    <button
                      onClick={() => setShowGuideModal(true)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={t('manage.guideButtonLabel')}
                      data-testid="manage-guide-button"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                      </svg>
                    </button>
                  </div>
                  {flashcardQuota && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {flashcardQuota.limit === null
                        ? t('manage.quotaUnlimited', { count: flashcardQuota.totalCount })
                        : t('manage.quotaLimited', { used: flashcardQuota.totalCount, limit: flashcardQuota.limit || FREE_FLASHCARD_LIMIT, remaining: flashcardQuota.remaining })}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {selectMode ? (
                    <>
                      {selectedCardIds.size > 0 && (
                        <button
                          onClick={handleBulkDelete}
                          className="flex items-center gap-1.5 bg-destructive text-destructive-foreground px-3 py-2 rounded-lg text-sm font-semibold hover:bg-destructive/90 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t('manage.deleteSelected', { count: selectedCardIds.size })}
                        </button>
                      )}
                      <button
                        onClick={exitSelectMode}
                        className="flex items-center gap-1.5 border border-border text-foreground px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
                      >
                        {t('common.cancel')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleQuotaGatedAction(() => setShowAddModal(true))}
                        className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        {t('common.add')}
                      </button>
                      <button
                        onClick={() => handleQuotaGatedAction(() => setShowBulkModal(true))}
                        className="flex items-center gap-1.5 border border-primary text-primary px-3 py-2 rounded-lg text-sm font-semibold hover:bg-primary/10 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {t('common.import')}
                      </button>
                      <button
                        onClick={() => handleQuotaGatedAction(() => setShowGenerateModal(true))}
                        className="flex items-center gap-1.5 border border-primary text-primary px-3 py-2 rounded-lg text-sm font-semibold hover:bg-primary/10 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                        </svg>
                        {t('common.generate')}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {flashcards.length > 0 && (
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={manageSearch}
                    onChange={(e) => setManageSearch(e.target.value)}
                    placeholder={t('manage.searchPlaceholder')}
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {manageSearch && (
                    <button
                      onClick={() => setManageSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )}

              {flashcards.length > 0 && (
                <div className="flex items-center gap-2 md:hidden">
                  {MOBILE_SORT_FIELDS.map((field) => {
                    const label = field === 'priority' ? t('flashcardModals.addCard.priorityLabel') : t('manage.columnProficiency');
                    const sortEntry = manageSortCriteria.find(c => c.field === field);
                    const sortIndex = manageSortCriteria.findIndex(c => c.field === field);
                    const ariaLabel = !sortEntry
                      ? t('manage.sortByField', { field: label })
                      : sortEntry.dir === 'asc'
                        ? t('manage.sortByFieldAscending', { field: label })
                        : t('manage.sortByFieldDescending', { field: label });
                    return (
                      <button
                        key={field}
                        type="button"
                        onClick={() => handleManageSort(field)}
                        data-testid={`manage-sort-${field}`}
                        aria-pressed={!!sortEntry}
                        aria-label={ariaLabel}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors select-none ${sortEntry ? 'border-primary bg-secondary text-primary' : 'border-border text-muted-foreground'}`}
                      >
                        {label}
                        {sortEntry && manageSortCriteria.length > 1 && (
                          <span className="text-sort-badge">{sortIndex + 1}</span>
                        )}
                        {sortEntry && (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sortEntry.dir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {manageSearch && filteredFlashcards.length !== flashcards.length && (
                <p className="text-sm text-muted-foreground">
                  {t('manage.showingOfTotal', { shown: filteredFlashcards.length, total: flashcards.length })}
                </p>
              )}

              {flashcards.length === 0 && pendingGenerations.length === 0 ? (
                <div className="bg-card rounded-lg shadow-md p-8 border border-border text-center">
                  <div className="text-6xl mb-4">📚</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{t('study.emptyTitle')}</h3>
                  {limitReached ? (
                    <>
                      <p className="text-muted-foreground">
                        {t('manage.limitReachedBody', { limit: FREE_FLASHCARD_LIMIT })}
                      </p>
                      <button
                        data-track="subscribe"
                        onClick={handleUpgrade}
                        disabled={upgradeLoading}
                        className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                      >
                        {upgradeLoading ? t('common.loading') : t('account.subscription.upgradeToPremium')}
                      </button>
                    </>
                  ) : (
                    <p className="text-muted-foreground">
                      {t('manage.emptyHint', { add: t('common.add'), import: t('common.import'), generate: t('common.generate') })}
                    </p>
                  )}
                </div>
              ) : filteredFlashcards.length === 0 && pendingGenerations.length === 0 ? (
                <div className="bg-card rounded-lg shadow-md p-8 border border-border text-center">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{t('manage.noMatchesTitle')}</h3>
                  <p className="text-muted-foreground text-sm">
                    {t('manage.noMatchesBody', { search: manageSearch })}
                  </p>
                </div>
              ) : (
                <>
                  <div className="md:overflow-x-auto">
                    <table className="w-full bg-card rounded-lg border border-border table-fixed max-md:block max-md:rounded-none max-md:border-0 max-md:bg-transparent">
                      <colgroup>
                        {selectMode && <col style={{ width: '4%' }} />}
                        <col style={{ width: selectMode ? '23%' : '25%' }} />
                        <col style={{ width: selectMode ? '25%' : '27%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '14%' }} />
                        {!selectMode && <col style={{ width: '10%' }} />}
                      </colgroup>
                      <thead className="max-md:hidden">
                        <tr className="border-b border-border">
                          {selectMode && (
                            <th className="p-4 text-center">
                              <input
                                type="checkbox"
                                checked={visibleFlashcards.length > 0 && visibleFlashcards.every(c => selectedCardIds.has(c._id))}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCardIds(prev => new Set([...prev, ...visibleFlashcards.map(c => c._id)]));
                                  } else {
                                    setSelectedCardIds(prev => {
                                      const next = new Set(prev);
                                      visibleFlashcards.forEach(c => next.delete(c._id));
                                      return next;
                                    });
                                  }
                                }}
                                className="w-4 h-4 accent-primary cursor-pointer"
                              />
                            </th>
                          )}
                          {manageColumnLabels.map(({ field, label, align }) => {
                            const sortEntry = manageSortCriteria.find(c => c.field === field);
                            const sortIndex = manageSortCriteria.findIndex(c => c.field === field);
                            return (
                              <th
                                key={field}
                                onClick={() => handleManageSort(field)}
                                className={`${align} p-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 select-none`}
                              >
                                <span className="inline-flex items-center gap-1">
                                  {label}
                                  {sortEntry && (
                                    <>
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sortEntry.dir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                                      </svg>
                                      {manageSortCriteria.length > 1 && (
                                        <span className="text-sort-badge text-muted-foreground">{sortIndex + 1}</span>
                                      )}
                                    </>
                                  )}
                                </span>
                              </th>
                            );
                          })}
                          {!selectMode && <th className="text-right p-4 text-sm font-semibold text-foreground">{t('manage.columnActions')}</th>}
                        </tr>
                      </thead>
                      <tbody className="max-md:block max-md:space-y-3">
                        {pendingGenerations.map(pending => (
                          <tr key={pending.id} className="md:border-b md:border-border md:last:border-0 bg-muted/20 max-md:block max-md:rounded-lg max-md:border max-md:border-border" data-testid={`pending-flashcard-${pending.id}`}>
                            <td colSpan={6} className="p-4 max-md:block">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full shrink-0" />
                                <span>{getPendingGenerationLabel(pending)}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {visibleFlashcards.map((card, idx) => {
                          return (
                          <React.Fragment key={card._id}>
                          <tr
                            className={`md:border-b md:border-border md:last:border-0 max-md:block max-md:relative max-md:overflow-hidden max-md:rounded-lg max-md:border max-md:border-border max-md:p-3 max-md:touch-pan-y ${selectMode ? 'cursor-pointer hover:bg-muted/30 select-none' : editingCard !== card._id ? 'cursor-pointer hover:bg-muted/20 select-none' : 'select-none'} ${selectMode && selectedCardIds.has(card._id) ? 'bg-primary/5' : ''}`}
                            onMouseDown={!selectMode && editingCard !== card._id ? () => handleRowLongPress(card._id, idx) : undefined}
                            onMouseUp={!selectMode && editingCard !== card._id ? cancelLongPress : undefined}
                            onMouseLeave={!selectMode && editingCard !== card._id ? cancelLongPress : undefined}
                            onTouchStart={!selectMode && editingCard !== card._id ? (e) => handleManageRowTouchStart(card, idx, e) : undefined}
                            onTouchMove={!selectMode && editingCard !== card._id ? (e) => handleManageRowTouchMove(card, e) : undefined}
                            onTouchEnd={!selectMode && editingCard !== card._id ? () => handleManageRowTouchEnd(card) : undefined}
                            onTouchCancel={!selectMode && editingCard !== card._id ? () => { cancelLongPress(); resetManageSwipe(); } : undefined}
                            onClick={selectMode ? (e) => handleSelectClick(card._id, idx, e) : editingCard !== card._id ? () => {
                              if (longPressJustFiredRef.current) {
                                longPressJustFiredRef.current = false;
                                return;
                              }
                              if (manageSwipeJustFiredRef.current) {
                                manageSwipeJustFiredRef.current = false;
                                return;
                              }
                              setExpandedManageExamplesId(prev => prev === card._id ? null : card._id);
                            } : undefined}
                          >
                            {!selectMode && editingCard !== card._id && (
                              <td className="hidden max-md:block max-md:absolute max-md:inset-0 max-md:p-0" aria-hidden="true">
                                <div className="flex h-full">
                                  <div
                                    className="flex-1 bg-destructive text-destructive-foreground flex items-center gap-2 pl-4"
                                    style={{ opacity: swipingCardId === card._id && manageSwipeOffset > 0 ? Math.min(1, manageSwipeOffset / MANAGE_SWIPE_TRIGGER_THRESHOLD) : 0 }}
                                  >
                                    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span className="text-sm font-semibold">{t('common.delete')}</span>
                                  </div>
                                  <div
                                    className="flex-1 bg-primary text-primary-foreground flex items-center justify-end gap-2 pr-4"
                                    style={{ opacity: swipingCardId === card._id && manageSwipeOffset < 0 ? Math.min(1, -manageSwipeOffset / MANAGE_SWIPE_TRIGGER_THRESHOLD) : 0 }}
                                  >
                                    <span className="text-sm font-semibold">{t('common.edit')}</span>
                                    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </div>
                                </div>
                              </td>
                            )}
                            {!selectMode && editingCard !== card._id && (
                              // Single continuous opaque layer the swipe slides as one piece,
                              // instead of each foreground <td> below carrying its own
                              // separately-sized background — those only cover their own tight
                              // content box, leaving the gaps around/between them (e.g. the
                              // padding around the priority badge) unprotected, letting the
                              // action panel bleed through in patches instead of a clean edge.
                              <td
                                className="hidden max-md:block max-md:absolute max-md:inset-0 max-md:p-0 max-md:bg-background"
                                style={manageSwipeStyle(card._id)}
                                aria-hidden="true"
                              />
                            )}
                            {selectMode && (
                              <td className="p-4 text-center max-md:block max-md:p-0 max-md:pb-2 max-md:text-left" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedCardIds.has(card._id)}
                                  onChange={() => toggleSelectCard(card._id)}
                                  className="w-4 h-4 accent-primary cursor-pointer"
                                />
                              </td>
                            )}
                            {editingCard === card._id ? (
                              <>
                                <td className="p-4 max-md:block max-md:p-0 max-md:pb-2">
                                  <input
                                    type="text"
                                    value={editForm.nativeText}
                                    onChange={(e) => setEditForm({ ...editForm, nativeText: e.target.value.slice(0, 50) })}
                                    className="w-full px-2 py-1 bg-background border border-border rounded text-foreground"
                                    maxLength={50}
                                  />
                                </td>
                                <td className="p-4 max-md:block max-md:p-0 max-md:pb-2">
                                  <div className="space-y-2">
                                    <input
                                      type="text"
                                      value={editForm.studyTextPrimary}
                                      onChange={(e) => setEditForm({ ...editForm, studyTextPrimary: e.target.value.slice(0, 50) })}
                                      className="w-full px-2 py-1 bg-background border border-border rounded text-foreground"
                                      placeholder={t('manage.primaryPlaceholder')}
                                      maxLength={50}
                                    />
                                    <input
                                      type="text"
                                      value={editForm.studyTextSecondary}
                                      onChange={(e) => setEditForm({ ...editForm, studyTextSecondary: e.target.value.slice(0, 50) })}
                                      className="w-full px-2 py-1 bg-background border border-border rounded text-foreground"
                                      placeholder={t('manage.secondaryPlaceholder')}
                                      maxLength={50}
                                    />
                                  </div>
                                </td>
                                <td className="p-4 text-center max-md:block max-md:p-0 max-md:pb-2 max-md:text-left">
                                  <div className="inline-block">
                                  <Select
                                    value={editForm.priority}
                                    onChange={(e) => setEditForm({ ...editForm, priority: parseInt(e.target.value) })}
                                    size="sm"
                                  >
                                    <option value={1}>{t('flashcardModals.editCard.priorityHighShort')}</option>
                                    <option value={2}>2</option>
                                    <option value={3}>3</option>
                                    <option value={4}>4</option>
                                    <option value={5}>{t('flashcardModals.editCard.priorityLowShort')}</option>
                                  </Select>
                                  </div>
                                </td>
                                <td className="p-4 text-center max-md:block max-md:p-0 max-md:pb-2 max-md:text-left">
                                  <span className="text-muted-foreground">{card.proficiency}/10</span>
                                </td>
                                <td className="p-4 text-center max-md:hidden"></td>
                                <td className="p-4 text-right max-md:block max-md:p-0">
                                  <button
                                    onClick={handleSaveEdit}
                                    className="text-success hover:text-success/80 mr-2"
                                  >
                                    {t('common.save')}
                                  </button>
                                  <button
                                    onClick={() => setEditingCard(null)}
                                    className="text-muted-foreground hover:text-foreground"
                                  >
                                    {t('common.cancel')}
                                  </button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td
                                  className="p-4 text-foreground max-md:block max-md:p-0 max-md:pb-1 max-md:pl-1.5 max-md:pr-14 max-md:font-semibold"
                                  style={manageSwipeStyle(card._id)}
                                >{card.nativeText}</td>
                                <td
                                  className="p-4 max-md:block max-md:p-0 max-md:pb-2"
                                  style={manageSwipeStyle(card._id)}
                                >
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (speakingManageCardId === card._id) {
                                            stopAudio();
                                          } else {
                                            playAudio(card.studyTextPrimary, user?.studyLanguage, card._id);
                                          }
                                        }}
                                        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors ${speakingManageCardId === card._id ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
                                        title={speakingManageCardId === card._id ? t('manage.stopAudioFor', { text: card.studyTextPrimary }) : t('manage.playAudioFor', { text: card.studyTextPrimary })}
                                        data-testid={`play-audio-${card._id}`}
                                      >
                                        <span className={speakingManageCardId === card._id ? 'text-primary' : 'text-foreground'}>
                                          {card.studyTextPrimary}
                                        </span>
                                        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          {speakingManageCardId === card._id ? (
                                            <>
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6" />
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </>
                                          ) : (
                                            <>
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5 6 9H3v6h3l5 4V5Z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.54 8.46a5 5 0 010 7.07" />
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M17.5 6.5a8 8 0 010 11" />
                                            </>
                                          )}
                                        </svg>
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); copyStudyText(card); }}
                                        className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${copiedCardId === card._id ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
                                        title={copiedCardId === card._id ? t('manage.copiedText', { text: card.studyTextPrimary }) : t('manage.copyText', { text: card.studyTextPrimary })}
                                        data-testid={`copy-study-${card._id}`}
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2" />
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 10h8a2 2 0 012 2v6a2 2 0 01-2 2h-8a2 2 0 01-2-2v-6a2 2 0 012-2z" />
                                        </svg>
                                      </button>
                                    </div>
                                    {card.studyTextSecondary && (
                                      <div
                                        className="flex items-center gap-1 pl-1.5"
                                        data-testid={`study-secondary-row-${card._id}`}
                                      >
                                        <span className="text-muted-foreground">{card.studyTextSecondary}</span>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); copySecondaryText(card); }}
                                          className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${copiedSecondaryCardId === card._id ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
                                          title={copiedSecondaryCardId === card._id ? t('manage.copiedText', { text: card.studyTextSecondary }) : t('manage.copyText', { text: card.studyTextSecondary })}
                                          data-testid={`copy-secondary-${card._id}`}
                                        >
                                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 10h8a2 2 0 012 2v6a2 2 0 01-2 2h-8a2 2 0 01-2-2v-6a2 2 0 012-2z" />
                                          </svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td
                                  className="p-4 text-center max-md:absolute max-md:top-3 max-md:right-3 max-md:block max-md:p-0"
                                  style={manageSwipeStyle(card._id)}
                                >
                                  <span
                                    className="inline-flex items-center justify-center px-1.5 h-6 rounded-full text-xs font-medium"
                                    style={{ backgroundColor: PRIORITY_COLORS[card.priority]?.bg, color: PRIORITY_COLORS[card.priority]?.text }}
                                  >P{card.priority}</span>
                                </td>
                                <td className="p-4 text-center max-md:hidden">
                                  <div className="flex items-center justify-center gap-1">
                                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-primary rounded-full"
                                        style={{ width: `${(card.proficiency / 10) * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-muted-foreground">{card.proficiency}</span>
                                  </div>
                                </td>
                                <td className="p-4 text-center max-md:hidden">
                                  <span className="text-xs text-muted-foreground">
                                    {card.proficiency >= 10 ? t('manage.mastered') : formatTimeUntil(card.nextReviewAt)}
                                  </span>
                                </td>
                                <td
                                  className="hidden max-md:absolute max-md:top-10 max-md:right-3 max-md:block max-md:p-0"
                                  style={manageSwipeStyle(card._id)}
                                >
                                  <span
                                    className="inline-flex items-center justify-center px-1.5 h-6 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                                    data-testid={`proficiency-badge-${card._id}`}
                                  >{card.proficiency}/10</span>
                                </td>
                                {!selectMode && (
                                  <td className="p-4 text-right max-md:hidden">
                                    <div className="inline-flex items-center gap-1">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleEdit(card); }}
                                        className="p-2 rounded-md text-primary hover:bg-primary/10 transition-colors"
                                        title={t('common.edit')}
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDelete(card._id); }}
                                        className="p-2 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
                                        title={t('common.delete')}
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </>
                            )}
                          </tr>
                          {expandedManageExamplesId === card._id && !selectMode && editingCard !== card._id && (
                            <tr className="max-md:block max-md:-mt-3 max-md:mb-3">
                              <td colSpan={6} className="px-4 pb-4 bg-muted/20 max-md:block max-md:rounded-b-lg max-md:border max-md:border-t-0 max-md:border-border">
                                <div className="border-t border-border pt-3">
                                  {manageExamplesLoadingId === card._id ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                      <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                                      {t('manage.generatingExamples')}
                                    </div>
                                  ) : card.examples && card.examples.length > 0 ? (
                                    <div className="space-y-2">
                                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('manage.examplesHeading')}</p>
                                      {card.examples.map((ex, i) => (
                                        <div key={i} className="text-sm">
                                          <span className="text-foreground font-medium">{ex.studyTextPrimary}</span>
                                          {ex.studyTextSecondary && <span className="text-muted-foreground ml-1.5">({ex.studyTextSecondary})</span>}
                                          {ex.nativeText !== ex.studyTextPrimary && (
                                            <span className="text-muted-foreground"> — {ex.nativeText}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-3">
                                      <p className="text-sm text-muted-foreground">{t('manage.noExamplesYet')}</p>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleManageGenerateExamples(card); }}
                                        className="text-sm text-primary hover:underline"
                                      >
                                        {t('common.generate')}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {hasMoreFlashcards && (
                    <div ref={manageLoadMoreRef} className="flex items-center justify-center py-4" data-testid="manage-load-more-sentinel">
                      <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" aria-label={t('manage.loadingMore')} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {bgToast && (
        <div
          role="status"
          data-testid="background-toast"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 max-w-md w-[calc(100%-2rem)] sm:w-auto px-4 py-3 rounded-lg shadow-xl border text-sm ${
            bgToast.type === 'success'
              ? 'bg-success-bg border-success-border text-success'
              : 'bg-error-bg border-error-border text-destructive'
          }`}
        >
          <span className="flex-1">{bgToast.message}</span>
          <button
            onClick={() => setBgToast(null)}
            className="shrink-0 opacity-70 hover:opacity-100"
            aria-label={t('manage.dismissNotification')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
